import {
  cpi,
  etc,
  marginPct,
  projectedCost,
  runRate,
  spi,
  tcpi,
  vac,
  workdaysBetween,
  type ContractFinance,
  type EacMode,
  type OccurrenceFinance,
} from '@forecastify/shared';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C58 · every derived figure on the dashboard, computed once.               │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THE PANELS READ THIS, THEY DO NOT COMPUTE.** Eight tiles, six bars, six footnotes, eleven
 * profitability rows and two table footers all quote CPI, EAC, the margin and the frame. Derived
 * per component they drift — and the way that shows up is one tile disagreeing with the bar under
 * it, which is the single worst thing a finance screen can do.
 *
 * ⚠️ **Pure, so it is tested rather than looked at.** The server sends figures; this turns them into
 * the ratios, and `evm.ts` owns each formula.
 */

export interface FinanceModel {
  readonly budget: number;
  readonly revenue: number;
  readonly revenueRetainer: number;
  readonly revenueBillable: number;
  readonly actualCost: number;
  readonly actualCostRetainer: number;
  readonly actualCostBillable: number;
  readonly externalCost: number;
  /** ⚠️ The PROJECT's own costs, which belong to no contract — see the wire. Never in `actualCost`. */
  readonly projectExternalCost: number;
  readonly plannedCost: number;
  readonly plannedValue: number;
  readonly earnedValue: number;
  readonly invoiced: number;
  readonly loggedHours: number;
  /** ⚠️ Earned HOURS — the numerator of CPI. See the model's own note on why not money. */
  readonly earnedHours: number;
  readonly plannedHours: number;

  readonly cpi: number | null;
  readonly spi: number | null;
  readonly eac: number | null;
  readonly etc: number | null;
  readonly vac: number | null;
  readonly tcpi: number | null;
  /** EV − AC. ⚠️ Negative means we spent more than the progress is worth. */
  readonly costVariance: number;
  /** EV − PV. ⚠️ Negative means behind the plan. */
  readonly scheduleVariance: number;

  /** Revenue − planned cost: what the plan would keep. */
  readonly plannedMargin: number;
  readonly plannedMarginPct: number | null;
  readonly forecastRevenue: number;
  /** The EAC, or the actual cost where there is no frame — what the forecast margin subtracts. */
  readonly forecastCost: number;
  /** Forecast revenue − EAC: what the close would keep. */
  readonly forecastMargin: number;
  readonly forecastMarginPct: number | null;

  /** Revenue per logged hour, and the list price of those same hours. */
  readonly actualHourlyRate: number | null;
  readonly targetHourlyRate: number | null;
  /**
   * 17 Oct 2026 · the revenue per ALLOCATED hour — the product owner, reversing U-3's list price:
   * the same revenue as `actualHourlyRate`, divided by the planned hours instead of the logged
   * ones (`decisions.md` FI-1). `null` with nothing planned.
   */
  readonly plannedHourlyRate: number | null;
  /** Revenue that has been delivered and not yet billed. ⚠️ Negative means billed in advance. */
  readonly workInProgress: number;
  readonly invoicedPct: number | null;

  /** The period the run-rate tiles are about — the current one, else the last closed one. */
  readonly currentPeriod: OccurrenceFinance | null;
  readonly periodWorkdays: number;
  readonly periodWorkdaysDone: number;
  readonly runRate: number;
  readonly allowedRunRate: number;
  readonly projectedPeriodCost: number;
  readonly periodOverrun: number;
  readonly periodPlannedMargin: number;
  readonly periodForecastMargin: number;
}

export function deriveFinance(
  view: ContractFinance,
  options: { readonly eacMode: EacMode },
): FinanceModel {
  const n = (value: string): number => Number(value);

  const budget = n(view.budget);
  const revenue = n(view.revenue);
  const actualCost = n(view.actualCost);
  const earnedValue = n(view.earnedValue);
  const plannedValue = n(view.plannedValue);
  const plannedCost = n(view.plannedCost);
  const invoiced = n(view.invoiced);
  const loggedHours = n(view.loggedHours);
  const earnedHours = n(view.earnedHours);

  /**
   * ⚠️⚠️ **ONE CPI FOR THE TILE AND THE EAC.** The tile is hours over hours (see below), and
   * `eac` used to recompute its own CPI from money — so *Trendtartó EAC* was not `BAC ÷` the CPI
   * printed beside it, and a reader checking it by hand found a different number. The hours CPI
   * stays (the mockup defines it in hours and the Dashboard tab divides the same pair); the money
   * EAC is `BAC ÷` it. The Dashboard's own EAC is untouched: it calls `eac` without an index, in
   * hours, where the two readings were always the same thing.
   */
  const costIndex = cpi(earnedHours, loggedHours);
  /**
   * ⚠️⚠️ 16 Oct 2026 · **THE EAC IS THE SERVER'S NOW, PER LINE ITEM AND SUMMED** (decisions a–e).
   * This used to be `eac(mode, BAC, AC, EV, CPI)` over the contract's totals — one CPI across a
   * fixed build and T&M support alike, and no external cost still to come. The server computes
   * each line item by its own kind and adds every external cost the switch counts in; the screen
   * picks the mode. The CPI tile above is still the contract's own hours ratio, for reading.
   */
  const forecast = view.forecast[options.eacMode];
  const estimateAtCompletion: number | null = n(forecast.cost);
  const forecastRevenue = n(forecast.revenue);
  const forecastCost = estimateAtCompletion;
  const forecastMargin = forecastRevenue - forecastCost;

  /**
   * ⚠️⚠️ **THE RUN-RATE TILES ARE ABOUT ONE PERIOD, NOT THE CONTRACT.** *Futamráta* and
   * *Előrejelzett túllépés* answer *"is this month going to fit"* — a contract-wide average would
   * answer a question nobody is asking while looking exactly as plausible.
   */
  const current =
    view.periods.find((period) => period.state === 'current') ??
    [...view.periods].reverse().find((period) => period.state === 'past') ??
    view.periods[0] ??
    null;

  const periodWorkdays =
    current === null ? 1 : workdaysBetween(current.startsOn, current.endsOn) || 1;
  const periodWorkdaysDone =
    current === null
      ? 0
      : view.today < current.startsOn
        ? 0
        : view.today > current.endsOn
          ? periodWorkdays
          : workdaysBetween(current.startsOn, view.today);

  const periodCost = current === null ? 0 : n(current.cost);
  const periodBudget = current === null ? 0 : n(current.budget);
  const periodRevenue = current === null ? 0 : n(current.revenue) + n(current.billableRevenue);
  const projected = projectedCost(periodCost, periodWorkdays, periodWorkdaysDone, periodBudget);

  return {
    budget,
    revenue,
    revenueRetainer: n(view.revenueRetainer),
    revenueBillable: n(view.revenueBillable),
    actualCost,
    actualCostRetainer: n(view.actualCostRetainer),
    actualCostBillable: n(view.actualCostBillable),
    externalCost: n(view.externalCost),
    projectExternalCost: n(view.projectExternalCost),
    plannedCost,
    plannedValue,
    earnedValue,
    invoiced,
    loggedHours,
    earnedHours,
    plannedHours: n(view.plannedHours),

    /**
     * ⚠️⚠️ **HOURS OVER HOURS, WHICH IS WHAT CPI IS.** `earnedValue / actualCost` divided
     * earned REVENUE by actual COST — a margin ratio with a CPI label on it, and it disagreed with
     * the Dashboard tab by a factor of twelve on the same contract. Both tabs divide the same two
     * numbers now.
     */
    cpi: costIndex,
    spi: spi(earnedValue, plannedValue, budget),
    eac: estimateAtCompletion,
    etc: etc(estimateAtCompletion, actualCost),
    vac: vac(budget, estimateAtCompletion),
    tcpi: tcpi(budget, earnedValue, actualCost),
    costVariance: earnedValue - actualCost,
    scheduleVariance: earnedValue - plannedValue,

    /* ⚠️ U-1 · `revenue` is the AGREED income, so this is the margin the plan keeps of what was
       agreed — not of what has been billed so far, which made it the cost of the plan mirrored. */
    plannedMargin: revenue - plannedCost,
    plannedMarginPct:
      marginPct(revenue, plannedCost) === null ? null : (revenue - plannedCost) / revenue,
    forecastRevenue,
    forecastCost,
    forecastMargin,
    forecastMarginPct: forecastRevenue > 0.4 ? forecastMargin / forecastRevenue : null,

    /* ⚠️ Revenue per hour, not cost per hour — what an hour of this work SOLD for. */
    actualHourlyRate: loggedHours > 0.004 ? revenue / loggedHours : null,
    targetHourlyRate: loggedHours > 0.004 ? n(view.targetRateTotal) / loggedHours : null,
    plannedHourlyRate: n(view.plannedHours) > 0.004 ? revenue / n(view.plannedHours) : null,
    workInProgress: revenue - invoiced,
    invoicedPct: revenue > 0.4 ? invoiced / revenue : null,

    currentPeriod: current,
    periodWorkdays,
    periodWorkdaysDone,
    runRate: runRate(periodCost, periodWorkdaysDone),
    /* ⚠️ The frame spread evenly — what a day is *allowed* to cost if the month is to fit. */
    allowedRunRate: periodWorkdays > 0 ? periodBudget / periodWorkdays : 0,
    projectedPeriodCost: projected,
    periodOverrun: projected - periodBudget,
    periodPlannedMargin: periodRevenue - periodBudget,
    periodForecastMargin: periodRevenue - projected,
  };
}
